import { forwardRef, DetailedHTMLProps, InputHTMLAttributes, useState, ReactNode } from "react";
import searchIcon from "src/assets/images/icon_search.svg";
import clsx from "clsx";

export interface AppInputProps
  extends Omit<DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>, "prefix"> {
  inputClass?: string;
  wrapperClass?: string;
  prefixNode?: ReactNode;
  suffixNode?: ReactNode;
}

export const AppInput = forwardRef<HTMLInputElement, AppInputProps>(
  ({ type, inputClass, wrapperClass, prefixNode, suffixNode, ...rest }, ref) => {
    const [focus, setFocus] = useState(false);
    const [hover, setHover] = useState(false);

    const style = {
      border: "1px solid #D8D8D8",
      boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.05)",
      background: "#FFFFFF",
    };

    const hoverStyle = {
      background:
        "linear-gradient(#ffffff, #ffffff) padding-box, linear-gradient(to right, #3A30DD, #FE3876) border-box",
      boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.05)",
      border: "1px solid transparent",
    };

    const inputElement = (
      <input
        {...rest}
        ref={ref}
        type={type}
        className={clsx(
          "appearance-none text-normal text-sm border-0 bg-transparent flex-1 h-full px-[0.625rem] focus-visible:outline-none",
          prefixNode && "pl-0",
          suffixNode && "pr-0",
          inputClass
        )}
        onBlur={() => setFocus(false)}
        onFocus={() => setFocus(true)}
      />
    );

    // Handle search type with built-in suffix
    if (type === "search") {
      return (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            alert("search");
          }}
          className="relative"
        >
          <div
            style={focus || hover ? hoverStyle : style}
            className="appearance-none text-normal text-sm border border-b3 rounded h-9 flex items-center transition-colors duration-300"
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
          >
            {inputElement}
            <button
              type="submit"
              className="h-7 w-7 mr-1 rounded-full flex items-center justify-center hover:bg-gray-800/10"
            >
              <img alt="..." src={searchIcon} />
            </button>
          </div>
        </form>
      );
    }

    // Handle inputs with prefix/suffix
    if (prefixNode || suffixNode) {
      return (
        <div
          style={focus || hover ? hoverStyle : style}
          className={clsx(
            "appearance-none text-normal text-sm border border-b3 rounded h-9 flex items-center transition-colors duration-300",
            wrapperClass
          )}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
        >
          {prefixNode && <div className="flex items-center pl-[0.625rem]">{prefixNode}</div>}
          {inputElement}
          {suffixNode && <div className="flex items-center pr-[0.625rem]">{suffixNode}</div>}
        </div>
      );
    }

    // Default input without prefix/suffix
    return (
      <input
        {...rest}
        ref={ref}
        type={type}
        style={focus || hover ? hoverStyle : style}
        className={clsx(
          "appearance-none text-normal text-sm border border-b3 rounded px-[0.625rem] h-9 flex items-center focus-visible:outline-none transition-colors duration-300",
          inputClass
        )}
        onBlur={() => setFocus(false)}
        onFocus={() => setFocus(true)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      />
    );
  }
);
